import React, { useMemo, useEffect, useState } from 'react';
import {
    TextField,
    Select,
    MenuItem,
    IconButton,
    CircularProgress,
    Box,
    Typography,
    Tooltip,
    Menu,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Paper,
    FormControl,
    InputLabel,
    Checkbox
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import SecurityIcon from '@mui/icons-material/Security';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import useUserStore from '../../state/userStore';
import { useFetchUsers, useDeleteUser, usePermanentDeleteUser, useReactivateUser, useFetchClasses } from '../../hooks/useUsers';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createUserInFirebase } from '../../firebaseUtils';
import configData from '../../configure';
import Table from '../../components/Table';
import AdminAssistant from '../Dashboard/AdminAssistant';
import UserProfileModule from './UserProfileModule';
import { getStudentColumns } from './configs/studentColumns';
import { getFacultyColumns } from './configs/facultyColumns';
import { getStaffColumns } from './configs/staffColumns';
import { getParentColumns } from './configs/parentColumns';
import { getAdminColumns } from './configs/adminColumns';
import { checkFirebaseUserStatus } from '../../firebaseUtils';

const UserModule = ({ userType, orgId, customColumns = [], fixedFacultyType, isKvFaculty }) => {
    const { axios, hasPermission, userRole, user: currentUser, token, facultyInfo } = useAuth();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // Zustand state
    const {
        openTabs,
        activeTab,
        activeTabId,
        addTab,
        closeTab,
        setActiveTab,
        setActiveTabId,
        searchTerms,
        setSearchTerm,
        selectedGrades,
        setSelectedGrade,
    } = useUserStore();

    const activeKvTab = (fixedFacultyType || 'samanvaykarta').toUpperCase();

    // ── KV Delete states ──────────────────────────────────────────────────────
    const [kvDeleteDialog, setKvDeleteDialog] = useState({ open: false, user: null, hasSubordinates: false, subordinates: [] });
    const [kvDeleting, setKvDeleting] = useState(false);
    const [replacementSupervisorId, setReplacementSupervisorId] = useState('');

    // Manage Classes States inside UserModule
    const [openClassesModal, setOpenClassesModal] = useState(false);
    const [selectedUserForClasses, setSelectedUserForClasses] = useState(null);
    const [selectedClasses, setSelectedClasses] = useState([]);
    const [classesSearchTerm, setClassesSearchTerm] = useState('');
    const [savingClasses, setSavingClasses] = useState(false);
    const handleOpenClassesModal = (row) => {
        setSelectedUserForClasses(row);
        const rawUserObj = row.user || row;
        const department = rawUserObj.additionalAttributes?.assignedClasses || row.additionalAttributes?.assignedClasses || '';
        // Don't filter by validClassIds here — classes may still be loading when modal opens,
        // which would silently drop previously assigned classes from the selection.
        const currentClasses = department && department !== 'Main Region'
            ? department.split(',').map(d => d.trim()).filter(Boolean)
            : [];
        setSelectedClasses(currentClasses);
        setClassesSearchTerm('');
        setOpenClassesModal(true);
    };

    const handleSaveClasses = async () => {
        if (!selectedUserForClasses) return;
        setSavingClasses(true);
        try {
            const rawUserObj = selectedUserForClasses.rawData?.user || selectedUserForClasses.user || selectedUserForClasses || {};
            const rawInfo = selectedUserForClasses.rawData?.facultyInfo || selectedUserForClasses.facultyInfo || {};
            const userId = rawUserObj.userId || selectedUserForClasses.userId;
            const facultyId = rawInfo.facultyId || selectedUserForClasses.facultyId || selectedUserForClasses.rawData?.facultyInfo?.facultyId;
            const currentTime = new Date().toISOString();

            const userPayload = {
                ...rawUserObj,
                userId,
                orgId: rawUserObj.orgId || orgId,
                userType: rawUserObj.userType || 'FACULTY',
                roleId: rawUserObj.roleId || `ROLE_FACULTY`,
                userStatus: rawUserObj.userStatus || 'ACTIVE',
                lastUpdatedBy: 'system',
                lastUpdatedAt: currentTime,
                addressDetails: rawUserObj.addressDetails || [{ addressType: 'CURRENT' }],
                additionalAttributes: {
                    ...(rawUserObj.additionalAttributes || {}),
                    assignedClasses: selectedClasses.join(', '),
                }
            };

            const facultyPayload = {
                ...rawInfo,
                facultyId,
                userId,
                department: '', // Clear department to favor additionalAttributes
                lastUpdatedBy: 'system',
                lastUpdatedAt: currentTime,
            };

            const updateData = {
                user: userPayload,
                studentInfo: null,
                parentInfo: null,
                staffInfo: null,
                facultyInfo: facultyPayload,
            };

            await axios.put(`${configData.UPDATE_USER}/${userId}`, updateData);

            toast.success('Classes successfully assigned to Nirvahak!');
            setOpenClassesModal(false);

            // Refetch table data to update grid live
            refetch();
            queryClient.invalidateQueries(['allFacultyHierarchy', orgId]);
            queryClient.invalidateQueries(['classStudentCounts', orgId]);

            // Sync with open details tab if currently open in state
            const isTabOpen = openTabs.some(t => t.userId === userId);
            if (isTabOpen) {
                const updatedUser = {
                    ...selectedUserForClasses,
                    department: '',
                    facultyInfo: facultyPayload,
                    additionalAttributes: {
                        ...(rawUserObj.additionalAttributes || {}),
                        assignedClasses: selectedClasses.join(', '),
                    },
                    user: userPayload,
                    rawData: {
                        ...selectedUserForClasses.rawData,
                        user: userPayload,
                        facultyInfo: facultyPayload
                    }
                };
                addTab(updatedUser, 'FACULTY');
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to update Nirvahak classes.');
        } finally {
            setSavingClasses(false);
        }
    };


    // Reset tabs when navigating between user type pages
    useEffect(() => {
        setActiveTab('listView');
        // Close all tabs from previous user type
        openTabs.forEach(tab => closeTab(tab.userId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userType]); // Only reset on userType change, not every time a tab is added

    const searchTerm = searchTerms[userType] || '';
    const selectedClass = selectedGrades[userType] || '';
    const currentPage = 1;
    const pageSize = 300; // Fetch up to 100 users per page for fast loading

    // React Query
    const { data: classesData, isLoading: loadingClasses } = useFetchClasses(orgId, axios, currentUser);
    const { data: usersData, isLoading: loadingUsers, error, refetch } = useFetchUsers(userType, orgId, axios, {
        classId: selectedClass,
        searchTerm,
        page: currentPage,
        pageSize,
        fixedFacultyType: isKvFaculty ? activeKvTab : fixedFacultyType,
        user: currentUser,
    });

    // Background query to fetch the entire list of organization faculty for subordinate counting (shared with HierarchyView)
    const { data: allFacultyData } = useQuery({
        queryKey: ['allFacultyHierarchy', orgId],
        queryFn: async () => {
            if (!orgId) return [];
            const allItems = [];
            let lastEvaluatedKey;
            const seenKeys = new Set();

            do {
                const response = await axios.get(`${configData.GET_USERS_BY_TYPE}/${orgId}`, {
                    params: {
                        userType: 'FACULTY',
                        pageSize: 500,
                        lastEvaluatedKey: lastEvaluatedKey || undefined,
                    },
                    headers: { Authorization: `Bearer ${token}` }
                });
                allItems.push(...(response.data?.items || response.data?.content || []));
                lastEvaluatedKey = response.data?.lastEvaluatedKey;
                if (lastEvaluatedKey && seenKeys.has(lastEvaluatedKey)) break;
                if (lastEvaluatedKey) seenKeys.add(lastEvaluatedKey);
            } while (lastEvaluatedKey);

            return allItems;
        },
        enabled: !!orgId && userType === 'FACULTY' && isKvFaculty,
        staleTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: false,
    });

    const hasParyavekshakSubordinate = useMemo(() => {
        return kvDeleteDialog.subordinates?.some(s => {
            const role = s.facultyInfo?.facultyType || s.facultyType;
            return role === 'PARYAVEKSHAK';
        });
    }, [kvDeleteDialog.subordinates]);

    const replacementOptions = useMemo(() => {
        if (!kvDeleteDialog.user || !allFacultyData) return [];
        const targetUser = kvDeleteDialog.user;
        const targetUserId = targetUser?.rawData?.user?.userId || targetUser?.user?.userId || targetUser?.userId;
        const targetRole = targetUser?.facultyInfo?.facultyType || targetUser?.facultyType || activeKvTab;

        return allFacultyData
            .filter(f => {
                const fId = f.user?.userId || f.userId;
                const fRole = f.facultyInfo?.facultyType || f.facultyType;
                if (targetRole === 'NIRVAHAK') {
                    // Nirvahaks must be replaced by other Nirvahaks to merge classes
                    return fRole === 'NIRVAHAK' && fId !== targetUserId;
                }
                if (hasParyavekshakSubordinate) {
                    // Paryavekshaks must be supervised by Samanvaykartas
                    return fRole === 'SAMANVAYKARTA' && fId !== targetUserId;
                } else {
                    // Nirvahaks can be supervised by Paryavekshak or Samanvaykarta
                    return (fRole === 'PARYAVEKSHAK' || fRole === 'SAMANVAYKARTA') && fId !== targetUserId;
                }
            })
            .map(f => ({
                id: f.user?.userId || f.userId,
                name: [f.user?.firstName, f.user?.middleName, f.user?.lastName].filter(Boolean).join(' ').trim() || f.user?.email || 'Unknown'
            }));
    }, [kvDeleteDialog.user, hasParyavekshakSubordinate, allFacultyData, activeKvTab]);

    const targetRole = kvDeleteDialog.user?.facultyInfo?.facultyType || kvDeleteDialog.user?.facultyType || activeKvTab;
    const replacementLabel = targetRole === 'NIRVAHAK' ? 'Nirvahak' : (hasParyavekshakSubordinate ? 'Samanvaykarta' : 'Paryavekshak/Samanvaykarta');


    // Fetch all classes in organization to map className -> classId
    const { data: allClasses, isLoading: loadingAllClasses } = useQuery({
        queryKey: ['allClassesComplete', orgId],
        queryFn: async () => {
            if (!orgId) return [];
            const response = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data || [];
        },
        enabled: !!orgId && userType === 'FACULTY' && isKvFaculty,
        staleTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: false,
    });

    const classes = useMemo(() => {
        if (!allClasses) return [];
        return allClasses.map(cls => {
            const section = (cls.sectionName && cls.sectionName !== 'null' && cls.sectionName !== 'undefined') ? String(cls.sectionName).trim() : '';
            const label = section ? `${cls.className} ${section}` : `${cls.className || ''}`.trim();
            return {
                value: cls.classId || cls.id,
                label: label || 'Unnamed Class'
            };
        }).sort((a, b) => a.label.localeCompare(b.label));
    }, [allClasses]);

    const filteredClassesList = useMemo(() => {
        // Filter out entries without a valid value (classId) to prevent crashes
        const validClasses = classes.filter(cls => cls.value != null);
        const query = classesSearchTerm.toLowerCase().trim();
        if (!query) return validClasses;
        return validClasses.filter(cls =>
            cls.label.toLowerCase().includes(query) ||
            String(cls.value).toLowerCase().includes(query)
        );
    }, [classes, classesSearchTerm]);

    // Fetch student counts per classId using the reliable GET_STUDENTS_BY_CLASS endpoint
    // This builds a classId -> count map that can be used for student count aggregation
    const { data: classIdToStudentCount } = useQuery({
        queryKey: ['classStudentCounts', orgId, (allClasses || []).map(c => c.classId).join(',')],
        queryFn: async () => {
            if (!orgId || !allClasses || allClasses.length === 0) return new Map();
            const countMap = new Map();

            const countClassStudents = async (classId) => {
                try {
                    const res = await axios.get(`${configData.GET_STUDENTS_BY_CLASS}/${classId}`, {
                        params: {
                            page: 0,
                            pageSize: 1000,
                        },
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (res.data?.totalElements !== undefined) return res.data.totalElements;
                    if (res.data?.totalCount !== undefined) return res.data.totalCount;
                    if (res.data?.total !== undefined) return res.data.total;

                    const students = res.data?.items || res.data?.content || res.data || [];
                    return Array.isArray(students) ? students.length : 0;
                } catch (err) {
                    console.error(`Failed to get count for class ${classId}:`, err);
                    return 0;
                }
            };

            const classIds = allClasses.map(cls => cls.classId).filter(Boolean);
            const chunkSize = 8;
            for (let i = 0; i < classIds.length; i += chunkSize) {
                const chunk = classIds.slice(i, i + chunkSize);
                const results = await Promise.all(chunk.map(classId =>
                    countClassStudents(classId).then(count => {
                        const clsObj = allClasses.find(c => c.classId === classId);
                        return { classId, className: clsObj?.className, count };
                    }).catch(() => ({ classId, count: 0 }))
                ));
                results.forEach(({ classId, className, count }) => {
                    if (classId) countMap.set(classId, count);
                    if (className) countMap.set(className.trim(), count);
                    if (className) countMap.set(className.trim().toLowerCase(), count);
                });
            }

            return countMap;
        },
        enabled: !!orgId && userType === 'FACULTY' && isKvFaculty && !!(allClasses && allClasses.length > 0),
        staleTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: false,
    });

    // O(N) map compiler to resolve faculty records by userId, email, employeeId, and phone number
    const facultyMap = React.useMemo(() => {
        const map = new Map();
        if (allFacultyData) {
            allFacultyData.forEach(f => {
                const userId = f.user?.userId || f.userId;
                const email = f.user?.email || f.email;
                const employeeId = f.facultyInfo?.employeeId || f.employeeId;
                const phoneNumber = f.user?.phoneNumber || f.phoneNumber;

                if (userId) map.set(String(userId).toLowerCase().trim(), f);
                if (email) map.set(String(email).toLowerCase().trim(), f);
                if (employeeId) map.set(String(employeeId).toLowerCase().trim(), f);
                if (phoneNumber) map.set(String(phoneNumber).toLowerCase().trim(), f);
            });
        }
        return map;
    }, [allFacultyData]);

    // O(N) map compiler to resolve student counts for Samanvaykarta, Paryavekshak, and Nirvahak
    const studentCountsMap = React.useMemo(() => {
        const nirvahakStudents = new Map(); // nirvahakUserId -> count
        const paryavekshakStudents = new Map(); // paryavekshakUserId -> count
        const samanvaykartaStudents = new Map(); // samanvaykartaId -> count

        if (!isKvFaculty || !allFacultyData || !allClasses || !classIdToStudentCount) {
            return {
                nirvahakStudents,
                paryavekshakStudents,
                samanvaykartaStudents
            };
        }

        // 1. Populate className -> classId lookup (supports Class ID, Class Name, Section Name, and Full Combined Label)
        const termToClassIds = new Map();
        const addMapping = (term, classId) => {
            if (!term || !classId) return;
            const clean = String(term).toLowerCase().trim();
            if (!termToClassIds.has(clean)) {
                termToClassIds.set(clean, []);
            }
            const arr = termToClassIds.get(clean);
            if (!arr.includes(classId)) {
                arr.push(classId);
            }
        };

        allClasses.forEach(cls => {
            if (cls.classId) {
                addMapping(cls.classId, cls.classId);
                if (cls.className) {
                    addMapping(cls.className, cls.classId);
                }
                if (cls.sectionName && cls.sectionName !== 'null' && cls.sectionName !== 'undefined') {
                    addMapping(cls.sectionName, cls.classId);
                }
                const section = (cls.sectionName && cls.sectionName !== 'null' && cls.sectionName !== 'undefined') ? String(cls.sectionName).trim() : '';
                const fullLabel = section ? `${cls.className} ${section}` : `${cls.className || ''}`.trim();
                addMapping(fullLabel, cls.classId);
            }
        });

        const paryavekshaksList = [];
        const nirvahaksList = [];

        // 2. Match Nirvahak to assigned classes and sum student counts
        allFacultyData.forEach(f => {
            const type = f.facultyInfo?.facultyType || f.facultyType;
            const userId = f.user?.userId || f.userId;
            const department = f.user?.additionalAttributes?.assignedClasses || f.additionalAttributes?.assignedClasses || '';

            if (type === 'NIRVAHAK') {
                nirvahaksList.push(f);
                let sumCount = 0;
                if (department) {
                    const depts = department.split(',').map(d => d.trim());
                    const matchedClassIds = new Set();
                    depts.forEach(dept => {
                        if (dept) {
                            matchedClassIds.add(dept);
                            matchedClassIds.add(dept.toLowerCase());
                        }
                        const ids = termToClassIds.get(dept.toLowerCase()) || [];
                        ids.forEach(id => matchedClassIds.add(id));
                    });
                    matchedClassIds.forEach(classId => {
                        sumCount += (classIdToStudentCount.get(classId) || 0);
                    });
                }
                if (userId) nirvahakStudents.set(String(userId).toLowerCase().trim(), sumCount);
            } else if (type === 'PARYAVEKSHAK') {
                paryavekshaksList.push(f);
            }
        });

        // 3. Calculate Paryavekshak (L1) student counts by summing subordinate Nirvahak counts
        paryavekshaksList.forEach(par => {
            const parId = par.user?.userId || par.userId;
            if (!parId) return;
            const cleanParId = String(parId).toLowerCase().trim();
            let sum = 0;
            nirvahaksList.forEach(nir => {
                const nirId = nir.user?.userId || nir.userId;
                const rawSupervisorId = nir.facultyInfo?.supervisorId || nir.supervisorId;
                const resolvedSupervisor = rawSupervisorId ? facultyMap.get(String(rawSupervisorId).toLowerCase().trim()) : null;
                const resolvedSupervisorId = resolvedSupervisor ? (resolvedSupervisor.user?.userId || resolvedSupervisor.userId) : null;

                if (
                    (resolvedSupervisorId && String(resolvedSupervisorId).toLowerCase().trim() === cleanParId) ||
                    (rawSupervisorId && String(rawSupervisorId).toLowerCase().trim() === cleanParId)
                ) {
                    const cleanNirId = nirId ? String(nirId).toLowerCase().trim() : '';
                    sum += (nirvahakStudents.get(cleanNirId) || 0);
                }
            });
            paryavekshakStudents.set(cleanParId, sum);
        });

        // 4. Calculate Samanvaykarta (L2) student counts by summing subordinate Paryavekshak counts
        allFacultyData.forEach(f => {
            const type = f.facultyInfo?.facultyType || f.facultyType;
            if (type === 'SAMANVAYKARTA') {
                const samId = f.user?.userId || f.userId;
                if (!samId) return;
                const cleanSamId = String(samId).toLowerCase().trim();
                let sum = 0;
                paryavekshaksList.forEach(par => {
                    const parId = par.user?.userId || par.userId;
                    const rawSupervisorId = par.facultyInfo?.supervisorId || par.supervisorId;
                    const resolvedSupervisor = rawSupervisorId ? facultyMap.get(String(rawSupervisorId).toLowerCase().trim()) : null;
                    const resolvedSupervisorId = resolvedSupervisor ? (resolvedSupervisor.user?.userId || resolvedSupervisor.userId) : null;

                    if (
                        (resolvedSupervisorId && String(resolvedSupervisorId).toLowerCase().trim() === cleanSamId) ||
                        (rawSupervisorId && String(rawSupervisorId).toLowerCase().trim() === cleanSamId)
                    ) {
                        const cleanParId = parId ? String(parId).toLowerCase().trim() : '';
                        sum += (paryavekshakStudents.get(cleanParId) || 0);
                    }
                });
                samanvaykartaStudents.set(cleanSamId, sum);
            }
        });

        return {
            nirvahakStudents,
            paryavekshakStudents,
            samanvaykartaStudents
        };
    }, [allFacultyData, allClasses, classIdToStudentCount, isKvFaculty, facultyMap]);

    // O(N) map compiler to resolve Paryavekshak and Nirvahak counts per supervisor
    const facultyCountsMap = React.useMemo(() => {
        const paryavekshaks = new Map(); // supervisorId -> count of Paryavekshaks supervised
        const nirvahaks = new Map();     // supervisorId -> count of Nirvahaks supervised
        const paryavekshaksList = [];

        if (!isKvFaculty || !allFacultyData) {
            return {
                paryavekshaks,
                samanvaykartaNirvahaks: new Map(),
                paryavekshakNirvahaks: new Map()
            };
        }

        allFacultyData.forEach(f => {
            const type = f.facultyInfo?.facultyType || f.facultyType;
            const rawSupId = f.facultyInfo?.supervisorId || f.supervisorId;
            const resolvedSupervisor = rawSupId ? facultyMap.get(String(rawSupId).toLowerCase().trim()) : null;
            const supId = resolvedSupervisor ? (resolvedSupervisor.user?.userId || resolvedSupervisor.userId) : rawSupId;

            if (type === 'PARYAVEKSHAK') {
                paryavekshaksList.push(f);
                if (supId) {
                    const cleanSupId = String(supId).toLowerCase().trim();
                    paryavekshaks.set(cleanSupId, (paryavekshaks.get(cleanSupId) || 0) + 1);
                }
            } else if (type === 'NIRVAHAK') {
                if (supId) {
                    const cleanSupId = String(supId).toLowerCase().trim();
                    nirvahaks.set(cleanSupId, (nirvahaks.get(cleanSupId) || 0) + 1);
                }
            }
        });

        // Compute total Nirvahaks under each Samanvaykarta (by aggregating child counts of their Paryavekshaks)
        const samanvaykartaNirvahaks = new Map(); // samanvaykartaId -> total Nirvahaks
        paryavekshaksList.forEach(par => {
            const parId = par.user?.userId || par.userId;
            const rawSupId = par.facultyInfo?.supervisorId || par.supervisorId;
            const resolvedSupervisor = rawSupId ? facultyMap.get(String(rawSupId).toLowerCase().trim()) : null;
            const supId = resolvedSupervisor ? (resolvedSupervisor.user?.userId || resolvedSupervisor.userId) : rawSupId;

            const parNirvahakCount = parId ? (nirvahaks.get(String(parId).toLowerCase().trim()) || 0) : 0;
            if (supId) {
                const cleanSupId = String(supId).toLowerCase().trim();
                samanvaykartaNirvahaks.set(cleanSupId, (samanvaykartaNirvahaks.get(cleanSupId) || 0) + parNirvahakCount);
            }
        });

        return {
            paryavekshaks,
            samanvaykartaNirvahaks,
            paryavekshakNirvahaks: nirvahaks
        };
    }, [allFacultyData, isKvFaculty, facultyMap]);



    const deleteMutation = useDeleteUser(axios);
    const reactivateMutation = useReactivateUser(axios);
    const permanentDeleteMutation = usePermanentDeleteUser(axios);
    const [statusFilter, setStatusFilter] = React.useState('ALL'); // 'ACTIVE', 'INACTIVE', 'ALL'

    const handleReactivate = async (userId) => {
        try {
            await reactivateMutation.mutateAsync({ userId });
            toast.success("User reactivated successfully!");
            refetch();
        } catch (err) {
            console.error("Reactivation failed:", err);
            toast.error("Failed to reactivate user.");
        }
    };

    const handlePermanentDelete = async (userId, email) => {
        setConfirmationDialog({
            isOpen: true,
            title: 'Delete Permanently',
            message: 'Are you sure you want to permanently delete this user? This will completely remove them from the database. This action is irreversible.',
            onConfirm: async () => {
                try {
                    await permanentDeleteMutation.mutateAsync({ userId, email });
                    toast.success("User permanently deleted.");
                    closeTab(userId);
                    refetch();
                } catch (err) {
                    console.error("Permanent delete failed:", err);
                    toast.error("Failed to delete user permanently.");
                }
            }
        });
    };

    const [selectedFacultyType, setSelectedFacultyType] = React.useState(() => {
        if (isKvFaculty) {
            return activeKvTab;
        }
        if (fixedFacultyType) {
            return fixedFacultyType.toUpperCase();
        }
        return 'ALL';
    });

    const uniqueFacultyTypes = React.useMemo(() => {
        if (userType !== 'FACULTY' || !usersData?.items) return [];
        const types = new Set();
        usersData.items.forEach(row => {
            if (row.userType === 'FACULTY' || row.facultyInfo) {
                const facType = row.facultyInfo?.facultyType || row.facultyType;
                if (facType) {
                    types.add(facType.toUpperCase());
                }
            }
        });
        return Array.from(types);
    }, [usersData?.items, userType]);

    React.useEffect(() => {
        if (isKvFaculty) {
            setSelectedFacultyType(activeKvTab);
        } else if (fixedFacultyType) {
            setSelectedFacultyType(fixedFacultyType.toUpperCase());
        } else {
            setSelectedFacultyType('ALL');
        }
    }, [userType, fixedFacultyType, isKvFaculty, activeKvTab]);

    // Auto-select first class for students if none selected
    React.useEffect(() => {
        if (userType === 'STUDENT' && !selectedClass && classesData?.length > 0) {
            setSelectedGrade('STUDENT', classesData[0].value);
        }
    }, [userType, selectedClass, classesData, setSelectedGrade]);

    const [selectedUsers, setSelectedUsers] = React.useState([]);

    // Firebase menu state
    const [firebaseMenuAnchorEl, setFirebaseMenuAnchorEl] = React.useState(null);
    const [isCheckingStatus, setIsCheckingStatus] = React.useState(false);
    const [selectionFirebaseStatus, setSelectionFirebaseStatus] = React.useState({ hasAccount: 0, noAccount: 0 });

    const handleFirebaseMenuOpen = async (event) => {
        const anchor = event.currentTarget;
        setFirebaseMenuAnchorEl(anchor);

        if (selectedUsers.length === 0) return;

        setIsCheckingStatus(true);
        try {
            const results = await Promise.all(selectedUsers.map(async (u) => {
                const email = u.email || u.user?.email;
                if (!email) return { email, exists: false };
                const exists = await checkFirebaseUserStatus(email);
                return { email, exists };
            }));

            const hasAccountCount = results.filter(r => r.exists).length;
            const noAccountCount = results.length - hasAccountCount;

            setSelectionFirebaseStatus({
                hasAccount: hasAccountCount,
                noAccount: noAccountCount
            });
        } catch (err) {
            console.error("Failed to check Firebase status:", err);
        } finally {
            setIsCheckingStatus(false);
        }
    };

    const handleFirebaseMenuClose = () => {
        setFirebaseMenuAnchorEl(null);
        setSelectionFirebaseStatus({ hasAccount: 0, noAccount: 0 });
    };

    // Summary Dialog State
    const [summaryInfo, setSummaryInfo] = React.useState({
        isOpen: false,
        type: 'ENABLE', // 'ENABLE' or 'DISABLE'
        title: '',
        successItems: [], // [{ email, name }]
        failedItems: [], // [{ email, error }]
        alreadyExistsCount: 0,
        messagesSentCount: 0,
        password: "egurukul@123456"
    });

    const [confirmationDialog, setConfirmationDialog] = React.useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null,
    });

    const getUserSearchText = (row) => {
        const values = [];

        const collect = (input, depth = 0) => {
            if (depth > 5 || input == null) return;

            if (typeof input === 'string' || typeof input === 'number' || typeof input === 'boolean') {
                values.push(String(input));
                return;
            }

            if (Array.isArray(input)) {
                input.forEach((item) => collect(item, depth + 1));
                return;
            }

            if (typeof input === 'object') {
                Object.values(input).forEach((value) => collect(value, depth + 1));
            }
        };

        collect(row);
        return values.join(' ').toLowerCase();
    };

    const filteredItems = useMemo(() => {
        let items = usersData?.items || [];

        // Apply status filter
        if (statusFilter !== 'ALL') {
            items = items.filter(row => {
                const status = row.user?.userStatus || row.userStatus || 'ACTIVE';
                const isInactive = status === 'INACTIVE' || status === 'inactive';
                return statusFilter === 'ACTIVE' ? !isInactive : isInactive;
            });
        }

        // Filter by Faculty Type if selected
        if (userType === 'FACULTY' && selectedFacultyType !== 'ALL') {
            items = items.filter(row => {
                const facType = row.facultyInfo?.facultyType || row.facultyType || '';
                return facType.toUpperCase() === selectedFacultyType.toUpperCase();
            });
        }

        // Apply KV Faculty hierarchy restriction for logged-in Faculty members
        if (isKvFaculty && userRole === 'FACULTY' && currentUser?.userId) {
            const loggedInUserId = String(currentUser.userId).toLowerCase().trim();
            const loggedInUserType = facultyInfo?.facultyType; // e.g. SAMANVAYKARTA, PARYAVEKSHAK, NIRVAHAK

            items = items.filter(row => {
                const rowUserId = String(row.user?.userId || row.userId || '').toLowerCase().trim();
                if (!rowUserId) return false;

                // 1. Always show the logged-in user's own row
                if (rowUserId === loggedInUserId) {
                    return true;
                }

                // 2. Resolve supervisor mappings
                const supervisorId = row.facultyInfo?.supervisorId || row.supervisorId;
                const resolvedSupervisor = supervisorId ? facultyMap.get(String(supervisorId).toLowerCase().trim()) : null;
                const resolvedSupervisorId = resolvedSupervisor ? String(resolvedSupervisor.user?.userId || resolvedSupervisor.userId || '').toLowerCase().trim() : '';
                const cleanSupervisorId = supervisorId ? String(supervisorId).toLowerCase().trim() : '';

                if (loggedInUserType === 'SAMANVAYKARTA') {
                    // Samanvaykarta sees:
                    // - Paryavekshaks directly supervised by them
                    // - Nirvahaks supervised by those Paryavekshaks
                    const rowFacultyType = row.facultyInfo?.facultyType || row.facultyType;
                    if (rowFacultyType === 'PARYAVEKSHAK') {
                        return resolvedSupervisorId === loggedInUserId || cleanSupervisorId === loggedInUserId;
                    }
                    if (rowFacultyType === 'NIRVAHAK') {
                        // Find the Paryavekshak (supervisor of the Nirvahak)
                        if (resolvedSupervisorId) {
                            const parSupervisor = facultyMap.get(resolvedSupervisorId);
                            const parSupervisorId = parSupervisor ? String(parSupervisor.user?.userId || parSupervisor.userId || '').toLowerCase().trim() : '';
                            const rawParSupervisorId = parSupervisor?.facultyInfo?.supervisorId || parSupervisor?.supervisorId;
                            const cleanParSupervisorId = rawParSupervisorId ? String(rawParSupervisorId).toLowerCase().trim() : '';
                            return parSupervisorId === loggedInUserId || cleanParSupervisorId === loggedInUserId;
                        }
                        if (cleanSupervisorId) {
                            const parSupervisor = facultyMap.get(cleanSupervisorId);
                            const parSupervisorId = parSupervisor ? String(parSupervisor.user?.userId || parSupervisor.userId || '').toLowerCase().trim() : '';
                            const rawParSupervisorId = parSupervisor?.facultyInfo?.supervisorId || parSupervisor?.supervisorId;
                            const cleanParSupervisorId = rawParSupervisorId ? String(rawParSupervisorId).toLowerCase().trim() : '';
                            return parSupervisorId === loggedInUserId || cleanParSupervisorId === loggedInUserId;
                        }
                    }
                } else if (loggedInUserType === 'PARYAVEKSHAK') {
                    // Paryavekshak sees:
                    // - Samanvaykarta supervising them
                    // - Nirvahaks directly supervised by them
                    const rowFacultyType = row.facultyInfo?.facultyType || row.facultyType;
                    if (rowFacultyType === 'SAMANVAYKARTA') {
                        const rawLoggedInSupId = facultyInfo?.supervisorId;
                        const resolvedLoggedInSup = rawLoggedInSupId ? facultyMap.get(String(rawLoggedInSupId).toLowerCase().trim()) : null;
                        const resolvedLoggedInSupId = resolvedLoggedInSup ? String(resolvedLoggedInSup.user?.userId || resolvedLoggedInSup.userId || '').toLowerCase().trim() : '';
                        const cleanLoggedInSupId = rawLoggedInSupId ? String(rawLoggedInSupId).toLowerCase().trim() : '';
                        return rowUserId === resolvedLoggedInSupId || rowUserId === cleanLoggedInSupId;
                    }
                    if (rowFacultyType === 'NIRVAHAK') {
                        return resolvedSupervisorId === loggedInUserId || cleanSupervisorId === loggedInUserId;
                    }
                } else if (loggedInUserType === 'NIRVAHAK') {
                    // Nirvahak sees:
                    // - Paryavekshak supervising them
                    // - Samanvaykarta grand-supervising them
                    const rowFacultyType = row.facultyInfo?.facultyType || row.facultyType;
                    const rawLoggedInSupId = facultyInfo?.supervisorId;
                    const resolvedLoggedInSup = rawLoggedInSupId ? facultyMap.get(String(rawLoggedInSupId).toLowerCase().trim()) : null;
                    const resolvedLoggedInSupId = resolvedLoggedInSup ? String(resolvedLoggedInSup.user?.userId || resolvedLoggedInSup.userId || '').toLowerCase().trim() : '';
                    const cleanLoggedInSupId = rawLoggedInSupId ? String(rawLoggedInSupId).toLowerCase().trim() : '';

                    if (rowFacultyType === 'PARYAVEKSHAK') {
                        return rowUserId === resolvedLoggedInSupId || rowUserId === cleanLoggedInSupId;
                    }
                    if (rowFacultyType === 'SAMANVAYKARTA') {
                        if (resolvedLoggedInSupId) {
                            const parSupervisor = facultyMap.get(resolvedLoggedInSupId);
                            const parSupervisorId = parSupervisor ? String(parSupervisor.user?.userId || parSupervisor.userId || '').toLowerCase().trim() : '';
                            const rawParSupervisorId = parSupervisor?.facultyInfo?.supervisorId || parSupervisor?.supervisorId;
                            const cleanParSupervisorId = rawParSupervisorId ? String(rawParSupervisorId).toLowerCase().trim() : '';
                            return rowUserId === parSupervisorId || rowUserId === cleanParSupervisorId;
                        }
                        if (cleanLoggedInSupId) {
                            const parSupervisor = facultyMap.get(cleanLoggedInSupId);
                            const parSupervisorId = parSupervisor ? String(parSupervisor.user?.userId || parSupervisor.userId || '').toLowerCase().trim() : '';
                            const rawParSupervisorId = parSupervisor?.facultyInfo?.supervisorId || parSupervisor?.supervisorId;
                            const cleanParSupervisorId = rawParSupervisorId ? String(rawParSupervisorId).toLowerCase().trim() : '';
                            return rowUserId === parSupervisorId || rowUserId === cleanParSupervisorId;
                        }
                    }
                }

                return false;
            });
        }

        const query = (searchTerm || '').trim().toLowerCase();
        if (!query) return items;
        return items.filter((row) => getUserSearchText(row).includes(query));
    }, [usersData?.items, searchTerm, userType, selectedFacultyType, isKvFaculty, userRole, currentUser, facultyInfo, facultyMap, statusFilter]);

    const sortedMappedItems = useMemo(() => {
        if (!isKvFaculty || !filteredItems) return filteredItems;

        return filteredItems.map(row => {
            const userId = row.user?.userId || row.userId;

            // Samanvaykarta counts
            const paryavekshakCount = facultyCountsMap.paryavekshaks.get(userId) || 0;
            const SamanvaykartaNirvahakCount = facultyCountsMap.samanvaykartaNirvahaks.get(userId) || 0;
            const SamanvaykartaStudentCount = studentCountsMap.samanvaykartaStudents.get(userId) || 0;

            // Paryavekshak relations
            const supervisorId = row.facultyInfo?.supervisorId || row.supervisorId;
            const sup = supervisorId ? facultyMap.get(String(supervisorId).toLowerCase().trim()) : null;
            const samanvaykartaName = sup ? `${sup.user?.firstName || sup.firstName || ''} ${sup.user?.lastName || sup.lastName || ''}`.trim() : '';
            const paryavekshakNirvahakCount = facultyCountsMap.paryavekshakNirvahaks.get(userId) || 0;
            const paryavekshakStudentCount = studentCountsMap.paryavekshakStudents.get(userId) || 0;

            // Nirvahak relations
            const grandSupId = sup?.facultyInfo?.supervisorId || sup?.supervisorId;
            const grandSup = grandSupId ? facultyMap.get(String(grandSupId).toLowerCase().trim()) : null;
            const grandSupervisorName = grandSup ? `${grandSup.user?.firstName || grandSup.firstName || ''} ${grandSup.user?.lastName || grandSup.lastName || ''}`.trim() : '';
            const paryavekshakName = sup ? `${sup.user?.firstName || sup.firstName || ''} ${sup.user?.lastName || sup.lastName || ''}`.trim() : '';
            const nirvahakStudentCount = studentCountsMap.nirvahakStudents.get(userId) || 0;

            // Centers / Classes
            const department = row.user?.additionalAttributes?.assignedClasses || row.additionalAttributes?.assignedClasses || '';
            const classIdToLabel = new Map(classes.map(c => [c.value, c.label]));
            const assignedCentersString = department
                ? department.split(',').map(d => d.trim()).map(id => classIdToLabel.get(id)).filter(Boolean).join(', ')
                : '';

            // Map variables based on activeKvTab for ease of access by unified accessors
            let derivedParyavekshakCount = 0;
            let derivedNirvahakCount = 0;
            let derivedStudentCount = 0;
            let derivedSamanvaykartaName = '';
            let derivedParyavekshakName = '';

            if (activeKvTab === 'SAMANVAYKARTA') {
                derivedParyavekshakCount = paryavekshakCount;
                derivedNirvahakCount = SamanvaykartaNirvahakCount;
                derivedStudentCount = SamanvaykartaStudentCount;
            } else if (activeKvTab === 'PARYAVEKSHAK') {
                derivedSamanvaykartaName = samanvaykartaName;
                derivedNirvahakCount = paryavekshakNirvahakCount;
                derivedStudentCount = paryavekshakStudentCount;
            } else if (activeKvTab === 'NIRVAHAK') {
                derivedSamanvaykartaName = grandSupervisorName;
                derivedParyavekshakName = paryavekshakName;
                derivedStudentCount = nirvahakStudentCount;
            }

            return {
                ...row,
                paryavekshakCount: derivedParyavekshakCount,
                nirvahakCount: derivedNirvahakCount,
                studentCount: derivedStudentCount,
                samanvaykartaName: derivedSamanvaykartaName,
                paryavekshakName: derivedParyavekshakName,
                assignedCentersString,
                // True when the Nirvahak has at least one class/center assigned.
                // When false, student count = 0
                // but it means "unassigned" not "genuinely zero students".
                hasClassesAssigned: !!department.trim(),
            };
        });
    }, [filteredItems, isKvFaculty, activeKvTab, facultyCountsMap, studentCountsMap, facultyMap, classes]);

    const totalStudentsCount = useMemo(() => {
        if (!isKvFaculty || !filteredItems) return 0;
        let sum = 0;
        filteredItems.forEach(row => {
            const userId = row.user?.userId || row.userId;
            if (activeKvTab === 'NIRVAHAK') {
                sum += (studentCountsMap.nirvahakStudents.get(userId) || 0);
            } else if (activeKvTab === 'PARYAVEKSHAK') {
                sum += (studentCountsMap.paryavekshakStudents.get(userId) || 0);
            } else if (activeKvTab === 'SAMANVAYKARTA') {
                sum += (studentCountsMap.samanvaykartaStudents.get(userId) || 0);
            }
        });
        return sum;
    }, [filteredItems, activeKvTab, isKvFaculty, studentCountsMap]);

    const handleSearchChange = (e) => {
        setSearchTerm(userType, e.target.value);
    };



    // ── KV Delete handler ─────────────────────────────────────────────────────
    const handleKvDeleteRequest = React.useCallback((userOrId, emailFallback) => {
        let user = null;
        let userId = '';

        if (typeof userOrId === 'string') {
            userId = userOrId;
            // Lookup full user object from cached list
            const found = (allFacultyData || []).find(f => String(f.user?.userId || f.userId || '').toLowerCase().trim() === String(userId).toLowerCase().trim()) ||
                (usersData?.items || []).find(f => String(f.user?.userId || f.userId || '').toLowerCase().trim() === String(userId).toLowerCase().trim()) ||
                (usersData?.content || []).find(f => String(f.user?.userId || f.userId || '').toLowerCase().trim() === String(userId).toLowerCase().trim());
            user = found || { user: { userId, email: emailFallback } };
        } else {
            user = userOrId;
            userId = user?.rawData?.user?.userId || user?.user?.userId || user?.userId;
        }

        const role = user?.facultyInfo?.facultyType || user?.facultyType || user?.user?.additionalAttributes?.facultyType || activeKvTab;
        let subordinates = [];
        let hasSubordinates = false;

        if (role === 'NIRVAHAK') {
            const rawUserObj = user.rawData?.user || user.user || user;
            const department = rawUserObj.additionalAttributes?.assignedClasses || user.additionalAttributes?.assignedClasses || '';
            const assignedClassesList = department && department !== 'Main Region'
                ? department.split(',').map(d => d.trim()).filter(Boolean)
                : [];
            subordinates = assignedClassesList;
            hasSubordinates = subordinates.length > 0;
        } else {
            // Check if this faculty has subordinates in the cached hierarchy data
            subordinates = allFacultyData ? allFacultyData.filter(f => {
                const rawSupId = f.facultyInfo?.supervisorId || f.supervisorId;
                if (!rawSupId) return false;
                // direct ID match
                if (String(rawSupId).toLowerCase().trim() === (userId || '').toLowerCase().trim()) return true;
                // resolved via facultyMap
                const resolved = facultyMap.get(String(rawSupId).toLowerCase().trim());
                const resolvedId = resolved ? (resolved.user?.userId || resolved.userId) : null;
                return resolvedId && resolvedId.toLowerCase().trim() === (userId || '').toLowerCase().trim();
            }) : [];
            hasSubordinates = subordinates.length > 0;
        }

        setKvDeleteDialog({ open: true, user, hasSubordinates, subordinates });
        setReplacementSupervisorId('');
    }, [allFacultyData, usersData, activeKvTab, facultyMap]);

    const handleKvDeleteConfirm = async () => {
        const user = kvDeleteDialog.user;
        if (!user) return;
        const userId = user?.rawData?.user?.userId || user?.user?.userId || user?.userId;
        const targetRole = user?.facultyInfo?.facultyType || user?.facultyType || user?.user?.additionalAttributes?.facultyType || activeKvTab;
        setKvDeleting(true);
        try {
            if (kvDeleteDialog.hasSubordinates) {
                if (!replacementSupervisorId) {
                    toast.warn(targetRole === 'NIRVAHAK' ? 'Please select a replacement Nirvahak.' : 'Please select a replacement supervisor.');
                    setKvDeleting(false);
                    return;
                }

                if (replacementSupervisorId === 'none') {
                    if (targetRole === 'NIRVAHAK') {
                        toast.info(`Proceeding without reassigning class(es).`);
                    } else {
                        const updatePromises = kvDeleteDialog.subordinates.map(async (sub) => {
                            const subId = sub.user?.userId || sub.userId;
                            const detailRes = await axios.get(`${configData.GET_USER_DETAILS}/${subId}`);
                            const detail = detailRes.data;
                            const payload = {
                                user: detail.user,
                                studentInfo: detail.studentInfo || null,
                                parentInfo: detail.parentInfo || null,
                                facultyInfo: {
                                    ...detail.facultyInfo,
                                    supervisorId: null
                                },
                                staffInfo: detail.staffInfo || null
                            };
                            await axios.put(`${configData.UPDATE_USER}/${subId}`, payload);
                        });
                        await Promise.all(updatePromises);
                        toast.success(`Cleared supervisor for ${kvDeleteDialog.subordinates.length} team member(s).`);
                    }
                } else if (targetRole === 'NIRVAHAK') {
                    // Fetch details of the replacement Nirvahak
                    const detailRes = await axios.get(`${configData.GET_USER_DETAILS}/${replacementSupervisorId}`);
                    const detail = detailRes.data;

                    // Extract existing assigned classes of the replacement Nirvahak
                    const repRawUser = detail.user || {};
                    const repExistingClassesStr = repRawUser.additionalAttributes?.assignedClasses || '';
                    const repExistingClasses = repExistingClassesStr
                        ? repExistingClassesStr.split(',').map(c => c.trim()).filter(Boolean)
                        : [];

                    // Merge and de-duplicate classes
                    const mergedClasses = Array.from(new Set([...repExistingClasses, ...kvDeleteDialog.subordinates]));
                    const currentTime = new Date().toISOString();

                    const userPayload = {
                        ...repRawUser,
                        userId: replacementSupervisorId,
                        orgId: repRawUser.orgId || orgId,
                        userType: repRawUser.userType || 'FACULTY',
                        roleId: repRawUser.roleId || `ROLE_FACULTY`,
                        userStatus: repRawUser.userStatus || 'ACTIVE',
                        lastUpdatedBy: 'system',
                        lastUpdatedAt: currentTime,
                        additionalAttributes: {
                            ...(repRawUser.additionalAttributes || {}),
                            assignedClasses: mergedClasses.join(', '),
                        }
                    };

                    const facultyPayload = {
                        ...(detail.facultyInfo || {}),
                        facultyId: detail.facultyInfo?.facultyId || detail.facultyId,
                        userId: replacementSupervisorId,
                        department: '', // Clear department to favor additionalAttributes
                        lastUpdatedBy: 'system',
                        lastUpdatedAt: currentTime,
                    };

                    const payload = {
                        user: userPayload,
                        studentInfo: detail.studentInfo || null,
                        parentInfo: detail.parentInfo || null,
                        facultyInfo: facultyPayload,
                        staffInfo: detail.staffInfo || null
                    };

                    await axios.put(`${configData.UPDATE_USER}/${replacementSupervisorId}`, payload);

                    // Sync details tab if open in state
                    const isTabOpen = openTabs.some(t => t.userId === replacementSupervisorId);
                    if (isTabOpen) {
                        const tabToUpdate = openTabs.find(t => t.userId === replacementSupervisorId);
                        const updatedUser = {
                            ...tabToUpdate,
                            department: '',
                            facultyInfo: facultyPayload,
                            additionalAttributes: {
                                ...(repRawUser.additionalAttributes || {}),
                                assignedClasses: mergedClasses.join(', '),
                            },
                            user: userPayload,
                            rawData: {
                                ...tabToUpdate.rawData,
                                user: userPayload,
                                facultyInfo: facultyPayload
                            }
                        };
                        addTab(updatedUser, 'FACULTY');
                    }

                    toast.success(`Merged and reassigned ${kvDeleteDialog.subordinates.length} class(es) successfully.`);
                } else {
                    const updatePromises = kvDeleteDialog.subordinates.map(async (sub) => {
                        const subId = sub.user?.userId || sub.userId;
                        const detailRes = await axios.get(`${configData.GET_USER_DETAILS}/${subId}`);
                        const detail = detailRes.data;
                        const payload = {
                            user: detail.user,
                            studentInfo: detail.studentInfo || null,
                            parentInfo: detail.parentInfo || null,
                            facultyInfo: {
                                ...detail.facultyInfo,
                                supervisorId: replacementSupervisorId
                            },
                            staffInfo: detail.staffInfo || null
                        };
                        await axios.put(`${configData.UPDATE_USER}/${subId}`, payload);
                    });
                    await Promise.all(updatePromises);
                    toast.success(`Reassigned ${kvDeleteDialog.subordinates.length} team members successfully.`);
                }
            }

            const detailRes = await axios.get(`${configData.GET_USER_DETAILS}/${userId}`);
            const detail = detailRes.data;

            const email = user?.rawData?.user?.email || user?.user?.email || user?.email || detail?.user?.email || detail?.email;
            if (email) {
                try {
                    const { deleteUserFromFirebaseBestEffort } = await import('../../firebaseUtils');
                    await deleteUserFromFirebaseBestEffort(email);
                } catch (e) {
                    console.warn('Firebase deletion failed', e);
                }
            }

            const updatedUserObj = {
                ...detail,
                user: {
                    ...detail.user,
                    userStatus: 'INACTIVE',
                }
            };
            await axios.put(`${configData.UPDATE_USER}/${userId}`, updatedUserObj);
            const typeLabel = userType.charAt(0) + userType.slice(1).toLowerCase();
            toast.success(`${typeLabel} deactivated successfully.`);
            setKvDeleteDialog({ open: false, user: null, hasSubordinates: false, subordinates: [] });
            setReplacementSupervisorId('');
            closeTab(userId);
            refetch();
            queryClient.invalidateQueries(['allFacultyHierarchy', orgId]);
            queryClient.invalidateQueries(['users']);
            if (targetRole === 'NIRVAHAK') {
                queryClient.invalidateQueries(['classStudentCounts', orgId]);
            }
        } catch (err) {
            console.error('KV delete error:', err);
            toast.error(`Delete/Reassignment failed: ${err?.response?.data?.message || err.message}`);
        } finally {
            setKvDeleting(false);
        }
    };

    const handleDeleteSelected = () => {
        if (selectedUsers.length === 0) return;

        setConfirmationDialog({
            isOpen: true,
            title: 'Delete Selected Users',
            message: `Are you sure you want to delete ${selectedUsers.length} selected user(s)? This will permanently remove their records from the system.`,
            onConfirm: async () => {
                const typeLabel = userType.charAt(0) + userType.slice(1).toLowerCase();
                toast.info(`Starting bulk deletion for ${selectedUsers.length} ${typeLabel}(s)...`);

                try {
                    const results = await Promise.all(selectedUsers.map(async (u) => {
                        const userId = u.userId || u.user?.userId;
                        const email = u.email || u.user?.email;
                        if (!userId) return { success: false };

                        try {
                            await deleteMutation.mutateAsync({ userId, email });
                            return { success: true };
                        } catch (err) {
                            return { success: false, error: err };
                        }
                    }));

                    const successCount = results.filter(r => r.success).length;
                    const failCount = results.length - successCount;

                    if (successCount > 0) {
                        toast.success(`Successfully deleted ${successCount} ${typeLabel}(s).`);
                    }
                    if (failCount > 0) {
                        toast.error(`Failed to delete ${failCount} ${typeLabel}(s).`);
                    }

                    setSelectedUsers([]);
                    refetch();
                } catch (err) {
                    console.error('Bulk delete failed', err);
                    toast.error('Bulk deletion encountered an unexpected error.');
                }
            }
        });
    };

    const handleBulkSignup = () => {
        if (selectedUsers.length === 0) return;
        const typeLabel = userType.charAt(0) + userType.slice(1).toLowerCase();

        setConfirmationDialog({
            isOpen: true,
            title: 'Enable Signup',
            message: `Are you sure you want to enable signup for ${selectedUsers.length} selected ${typeLabel}(s)? They will be sent welcome messages with default login details.`,
            onConfirm: async () => {
                let successItems = [];
                let alreadyExistsItems = [];
                let failedItems = [];
                let messagesSentCount = 0;
                const defaultPassword = "egurukul@123456";

                toast.info(`Enabling signup for ${selectedUsers.length} ${typeLabel}(s)...`);

                for (const u of selectedUsers) {
                    const email = u.email || u.user?.email;
                    // Robust userId detection: prioritize nested user.userId which is the primary Identity UUID
                    // eslint-disable-next-line no-unused-vars
                    const recipientUserId = u.user?.userId || u.userId || u.studentInfo?.user?.userId || u.studentInfo?.userId || u.facultyInfo?.userId || u.staffInfo?.userId;
                    // CRITICAL: Always use the Authenticated User's Org ID (UUID) for messaging consistency
                    const effectiveOrgId = currentUser?.orgId || orgId;
                    const name = getFullName(u);

                    if (!email) {
                        console.warn(`Skipping ${userType} without email:`, u);
                        failedItems.push({ email: 'Unknown (Missing)', error: 'Missing Email' });
                        continue;
                    }

                    try {
                        await createUserInFirebase(email, defaultPassword);
                        successItems.push({ email, name });

                        // Automated Inbox Welcome Message
                        if (email && effectiveOrgId) {
                            try {
                                const finalOrgId = String(effectiveOrgId);

                                // Identity Resolution: Fetch the primary User Identity UUID by email
                                const identityRes = await axios.get(`${configData.GET_USER_BY_EMAIL_WITH_PARAM}/${email.replace(/@/g, '%40')}`);
                                const resolvedRecipientId = identityRes.data?.user?.userId;

                                if (!resolvedRecipientId) {
                                    throw new Error("Could not resolve primary Identity ID for email: " + email);
                                }

                                const welcomePayload = {
                                    subject: "Welcome to eGurukul: Your Login Details",
                                    body: `<div style="font-family: sans-serif; padding: 20px; border: 1px solid #e2e8f0; border-radius: 12px; max-width: 500px; background: #f8fafc;">
                                        <h2 style="color: #4f46e5; margin-top: 0;">Welcome to eGurukul!</h2>
                                        <p>Hello <strong>${name}</strong>,</p>
                                        <p>Your institutional account has been successfully enabled. You can now access your dashboard using the credentials below:</p>
                                        <div style="background: white; padding: 15px; border-radius: 8px; border-left: 4px solid #4f46e5; margin: 20px 0;">
                                            <p style="margin: 5px 0;"><strong>Username:</strong> ${email}</p>
                                            <p style="margin: 5px 0;"><strong>Default Password:</strong> ${defaultPassword}</p>
                                        </div>
                                        <p style="margin-bottom: 0;">Happy Learning,<br/><strong>Institution Administration</strong></p>
                                    </div>`,
                                    conversationId: null,
                                    orgId: finalOrgId,
                                    targetType: 'INDIVIDUAL',
                                    targetId: String(resolvedRecipientId)
                                };

                                await axios.post(configData.INBOX_SEND, welcomePayload, {
                                    params: { userId: currentUser?.userId }
                                });

                                messagesSentCount++;
                            } catch (msgErr) {
                                console.error(`[Inbox] FAILED for ${email}:`, msgErr.response?.data || msgErr.message);
                                toast.error(`Message delivery failed for ${email}`);
                            }
                        } else {
                            const reason = !email ? "Missing Email" : "Missing Org ID";
                            console.warn(`[Inbox] SKIPPED for ${email}: ${reason}`);
                        }
                    } catch (err) {
                        if (err.code === 'auth/email-already-in-use') {
                            alreadyExistsItems.push({ email, name });
                        } else {
                            console.error(`Signup failed for ${email}:`, err);
                            failedItems.push({ email, error: err.message || 'Unknown Error' });
                        }
                    }
                }

                setSummaryInfo({
                    isOpen: true,
                    type: 'ENABLE',
                    title: 'Signup Activation Summary',
                    successItems,
                    failedItems,
                    alreadyExistsItems,
                    messagesSentCount,
                    password: defaultPassword
                });

                setSelectedUsers([]);
                refetch();
            }
        });
    };

    const handleBulkRemoveFirebase = () => {
        if (selectedUsers.length === 0) return;
        const typeLabel = userType.charAt(0) + userType.slice(1).toLowerCase();

        setConfirmationDialog({
            isOpen: true,
            title: 'Disable Signup',
            message: `Are you sure you want to disable signup for ${selectedUsers.length} selected ${typeLabel}(s)? They will lose access to their dashboards.`,
            onConfirm: async () => {
                let successItems = [];
                let failedItems = [];

                toast.info(`Disabling signup for ${selectedUsers.length} ${typeLabel}(s)...`);

                const { deleteUserFromFirebaseBestEffort } = await import('../../firebaseUtils');

                for (const u of selectedUsers) {
                    const email = u.email || u.user?.email;
                    const name = getFullName(u);
                    if (!email) {
                        console.warn(`Skipping ${userType} without email:`, u);
                        failedItems.push({ email: 'Unknown (Missing)', error: 'Missing Email' });
                        continue;
                    }

                    try {
                        await deleteUserFromFirebaseBestEffort(email);
                        successItems.push({ email, name });
                    } catch (err) {
                        console.error(`Firebase removal failed for ${email}:`, err);
                        const errorMsg = err.code === 'auth/wrong-password'
                            ? 'Account exists but password has been changed. Must remove manually from Firebase Console.'
                            : (err.message || 'Removal Failed');
                        failedItems.push({ email, error: errorMsg });
                    }
                }

                setSummaryInfo({
                    isOpen: true,
                    type: 'DISABLE',
                    title: 'Signup Removal Summary',
                    successItems,
                    failedItems,
                    alreadyExistsItems: [],
                    messagesSentCount: 0,
                    password: ''
                });

                setSelectedUsers([]);
                refetch();
            }
        });
    };


    const getFullName = (u) => {
        if (!u) return 'User';
        const userObj = u.user || u;
        return `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim() || 'User';
    };



    const rolePermissions = {
        STUDENT: 'STUDENTS',
        FACULTY: 'FACULTY',
        STAFF: 'FACULTY',
        PARENT: 'ADMIN', // Default to ADMIN for now if specific PARENTS permission doesn't exist
        ADMIN: 'ADMIN',
    };

    const currentPermission = rolePermissions[userType];

    const defaultColumns = useMemo(() => {
        if (customColumns.length > 0) return customColumns;

        const params = {
            hasPermission,
            handleDelete: handleKvDeleteRequest,
            handleReactivate,
            handlePermanentDelete,
            isLoading: deleteMutation.isLoading || reactivateMutation.isLoading || permanentDeleteMutation.isLoading,
            isKvFaculty,
            orgId,
        };

        let cols = [];
        switch (userType) {
            case 'STUDENT':
                cols = getStudentColumns(params);
                break;
            case 'FACULTY':
                cols = getFacultyColumns(params);
                break;
            case 'STAFF':
                cols = getStaffColumns(params);
                break;
            case 'PARENT':
                cols = getParentColumns(params);
                break;
            case 'ADMIN':
                cols = getAdminColumns(params);
                break;
            default:
                cols = [
                    { header: 'ID', render: (row) => row.userId || 'N/A' },
                    { header: 'Name', render: (row) => `${row.firstName || ''} ${row.lastName || ''}` },
                    { header: 'Email', render: (row) => row.email || 'N/A' },
                ];
        }

        // Apply KV Faculty specific customization
        if (isKvFaculty && userType === 'FACULTY') {
            // Remove: loginaccess, department, dob, gender, email, city
            cols = cols.filter(col => {
                const headerLower = (col.header || '').toLowerCase();
                return !(
                    headerLower === 'login access' ||
                    headerLower === 'department' ||
                    headerLower === 'date of birth' ||
                    headerLower === 'gender' ||
                    headerLower === 'email' ||
                    headerLower === 'city'
                );
            });

            // 1. Combine First Name and Last Name into a premium Full Name column with initial avatar
            const firstNameIndex = cols.findIndex(col => (col.header || '').toLowerCase() === 'first name');
            const lastNameIndex = cols.findIndex(col => (col.header || '').toLowerCase() === 'last name');
            if (firstNameIndex !== -1 && lastNameIndex !== -1) {
                const nameCol = {
                    header: 'Name',
                    render: (row) => {
                        const first = row.user?.firstName || row.firstName || '';
                        const last = row.user?.lastName || row.lastName || '';
                        const fullName = `${first} ${last}`.trim() || 'N/A';
                        const initials = `${first[0] || ''}${last[0] || ''}`.toUpperCase();

                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    backgroundColor: '#eff6ff',
                                    color: '#1d4ed8',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 700,
                                    fontSize: '12px',
                                    fontFamily: 'var(--app-font)',
                                    border: '1px solid #bfdbfe'
                                }}>
                                    {initials || 'U'}
                                </Box>
                                <Typography sx={{
                                    fontWeight: 600,
                                    color: '#0f172a',
                                    fontSize: '14px',
                                    fontFamily: 'var(--app-font)'
                                }}>
                                    {fullName}
                                </Typography>
                            </Box>
                        );
                    }
                };
                cols.splice(firstNameIndex, 2, nameCol);
            }

            // 2. Format Employee ID into monospace grey micro-badge
            const empIdIndex = cols.findIndex(col => (col.header || '').toLowerCase() === 'employee id');
            if (empIdIndex !== -1) {
                cols[empIdIndex] = {
                    header: 'Employee ID',
                    render: (row) => {
                        const id = row.facultyInfo?.employeeId || row.staffInfo?.employeeId;
                        return id ? (
                            <Box sx={{
                                fontFamily: 'monospace',
                                backgroundColor: '#f8fafc',
                                px: 1.2,
                                py: 0.5,
                                borderRadius: '6px',
                                display: 'inline-block',
                                color: '#475569',
                                fontSize: '12.5px',
                                border: '1px solid #e2e8f0',
                                fontWeight: 600
                            }}>
                                {id}
                            </Box>
                        ) : 'N/A';
                    }
                };
            }

            // 3. Format Phone Number with subtle emoji
            const phoneIndex = cols.findIndex(col => (col.header || '').toLowerCase() === 'phone');
            if (phoneIndex !== -1) {
                cols[phoneIndex] = {
                    header: 'Phone Number',
                    render: (row) => {
                        const num = row.user?.phoneNumber || row.phoneNumber;
                        return num ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: '#334155', fontSize: '13.5px', fontWeight: 500 }}>
                                📞 <span>{num}</span>
                            </Box>
                        ) : 'N/A';
                    }
                };
            }

            // 4. Format Faculty Type into dynamic premium badge pill
            const facTypeIndex = cols.findIndex(col => (col.header || '').toLowerCase() === 'faculty type');
            if (facTypeIndex !== -1) {
                cols[facTypeIndex] = {
                    header: 'Faculty Role',
                    render: (row) => {
                        const type = row.facultyInfo?.facultyType || row.facultyType || 'N/A';
                        const typeClean = type.replace(/_/g, ' ');
                        return (
                            <Box sx={{
                                display: 'inline-flex',
                                px: 2,
                                py: 0.5,
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                backgroundColor: type === 'SAMANVAYKARTA' ? '#f0fdf4' : type === 'PARYAVEKSHAK' ? '#eff6ff' : '#fff7ed',
                                color: type === 'SAMANVAYKARTA' ? '#166534' : type === 'PARYAVEKSHAK' ? '#1e40af' : '#9a3412',
                                border: `1px solid ${type === 'SAMANVAYKARTA' ? '#bbf7d0' : type === 'PARYAVEKSHAK' ? '#bfdbfe' : '#fed7aa'}`
                            }}>
                                {typeClean}
                            </Box>
                        );
                    }
                };
            }
            // If active tab is Samanvaykarta, insert Paryavekshak, Nirvahak, and Student counts
            if (activeKvTab === 'SAMANVAYKARTA') {
                const facultyTypeIndex = cols.findIndex(col => (col.header || '').toLowerCase() === 'faculty role');
                const countCols = [
                    {
                        header: 'Paryavekshak',
                        accessor: 'paryavekshakCount',
                        render: (row) => {
                            const val = row.paryavekshakCount;
                            return (
                                <Box sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '28px',
                                    height: '28px',
                                    borderRadius: '50%',
                                    backgroundColor: val > 0 ? '#eff6ff' : '#f8fafc',
                                    color: val > 0 ? '#1d4ed8' : '#64748b',
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    border: val > 0 ? '1px solid #bfdbfe' : '1px solid #cbd5e1'
                                }}>
                                    {val}
                                </Box>
                            );
                        }
                    },
                    {
                        header: 'Nirvahak',
                        accessor: 'nirvahakCount',
                        render: (row) => {
                            const val = row.nirvahakCount;
                            return (
                                <Box sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '28px',
                                    height: '28px',
                                    borderRadius: '50%',
                                    backgroundColor: val > 0 ? '#ecfdf5' : '#f8fafc',
                                    color: val > 0 ? '#047857' : '#64748b',
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    border: val > 0 ? '1px solid #a7f3d0' : '1px solid #cbd5e1'
                                }}>
                                    {val}
                                </Box>
                            );
                        }
                    },
                    {
                        header: 'Students',
                        accessor: 'studentCount',
                        render: (row) => {
                            const val = row.studentCount;
                            return (
                                <Box sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '32px',
                                    height: '24px',
                                    borderRadius: '12px',
                                    backgroundColor: val > 0 ? '#eff6ff' : '#f8fafc',
                                    color: val > 0 ? '#1d4ed8' : '#64748b',
                                    fontWeight: 700,
                                    fontSize: '12.5px',
                                    border: val > 0 ? '1px solid #bfdbfe' : '1px solid #e2e8f0',
                                    px: 1
                                }}>
                                    {val}
                                </Box>
                            );
                        }
                    }
                ];

                if (facultyTypeIndex !== -1) {
                    cols.splice(facultyTypeIndex + 1, 0, ...countCols);
                } else {
                    cols.push(...countCols);
                }
            } else if (activeKvTab === 'PARYAVEKSHAK') {
                // If active tab is Paryavekshak, insert Samanvaykarta, Nirvahak direct counts and Student counts
                const facultyTypeIndex = cols.findIndex(col => (col.header || '').toLowerCase() === 'faculty role');
                const countCols = [
                    {
                        header: 'Samanvaykarta',
                        accessor: 'samanvaykartaName',
                        render: (row) => {
                            const name = row.samanvaykartaName;
                            return name || <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '13px' }}>Not Assigned</Typography>;
                        }
                    },
                    {
                        header: 'Nirvahak',
                        accessor: 'nirvahakCount',
                        render: (row) => {
                            const val = row.nirvahakCount;
                            return (
                                <Box sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '28px',
                                    height: '28px',
                                    borderRadius: '50%',
                                    backgroundColor: val > 0 ? '#ecfdf5' : '#f8fafc',
                                    color: val > 0 ? '#047857' : '#64748b',
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    border: val > 0 ? '1px solid #a7f3d0' : '1px solid #cbd5e1'
                                }}>
                                    {val}
                                </Box>
                            );
                        }
                    },
                    {
                        header: 'Students',
                        accessor: 'studentCount',
                        render: (row) => {
                            const val = row.studentCount;
                            return (
                                <Box sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '32px',
                                    height: '24px',
                                    borderRadius: '12px',
                                    backgroundColor: val > 0 ? '#eff6ff' : '#f8fafc',
                                    color: val > 0 ? '#1d4ed8' : '#64748b',
                                    fontWeight: 700,
                                    fontSize: '12.5px',
                                    border: val > 0 ? '1px solid #bfdbfe' : '1px solid #e2e8f0',
                                    px: 1
                                }}>
                                    {val}
                                </Box>
                            );
                        }
                    }
                ];

                if (facultyTypeIndex !== -1) {
                    cols.splice(facultyTypeIndex + 1, 0, ...countCols);
                } else {
                    cols.push(...countCols);
                }
            } else if (activeKvTab === 'NIRVAHAK') {
                // If active tab is Nirvahak, insert direct Student counts
                const facultyTypeIndex = cols.findIndex(col => (col.header || '').toLowerCase() === 'faculty role');

                const centersClassesCol = {
                    header: 'Centers / Classes',
                    accessor: 'assignedCentersString',
                    render: (row) => {
                        const depts = row.assignedCentersString
                            ? row.assignedCentersString.split(',').map(d => d.trim()).filter(Boolean)
                            : [];

                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '240px' }}>
                                    {depts.length > 0 ? (
                                        depts.map((d, index) => (
                                            <Chip
                                                key={index}
                                                label={d}
                                                size="small"
                                                sx={{
                                                    bgcolor: '#eff6ff',
                                                    color: '#1d4ed8',
                                                    fontWeight: 600,
                                                    fontSize: '11px',
                                                    border: '1px solid #bfdbfe',
                                                    height: '20px'
                                                }}
                                            />
                                        ))
                                    ) : (
                                        <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '13px', fontStyle: 'italic' }}>
                                            None Assigned
                                        </Typography>
                                    )}
                                </Box>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenClassesModal(row);
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        fontSize: '11px',
                                        py: 0.2,
                                        px: 1,
                                        height: '22px',
                                        minWidth: 'auto',
                                        borderColor: '#cbd5e1',
                                        color: '#475569',
                                        borderRadius: '6px',
                                        '&:hover': {
                                            borderColor: '#0070D2',
                                            color: '#0070D2',
                                            bgcolor: '#f0f9ff'
                                        }
                                    }}
                                >
                                    ✏️ Add
                                </Button>
                            </Box>
                        );
                    }
                };

                const studentsCol = {
                    header: 'Students',
                    accessor: 'studentCount',
                    render: (row) => {
                        const val = row.studentCount;
                        // When additionalAttributes is null and no department is set,
                        // student count is 0 because NO classes are assigned — not because
                        // zero students exist. Show a distinct indicator so admins know.
                        if (!row.hasClassesAssigned) {
                            return (
                                <Box sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    px: 1.2,
                                    py: 0.3,
                                    borderRadius: '10px',
                                    backgroundColor: '#fff7ed',
                                    color: '#c2410c',
                                    fontWeight: 600,
                                    fontSize: '11px',
                                    border: '1px solid #fed7aa',
                                    gap: 0.4,
                                    whiteSpace: 'nowrap',
                                }}>
                                    ⚠ Set classes
                                </Box>
                            );
                        }
                        return (
                            <Box sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '32px',
                                height: '24px',
                                borderRadius: '12px',
                                backgroundColor: val > 0 ? '#eff6ff' : '#f8fafc',
                                color: val > 0 ? '#1d4ed8' : '#64748b',
                                fontWeight: 700,
                                fontSize: '12.5px',
                                border: val > 0 ? '1px solid #bfdbfe' : '1px solid #e2e8f0',
                                px: 1
                            }}>
                                {val}
                            </Box>
                        );
                    }
                };

                const supervisorCol = {
                    header: 'Paryavekshak',
                    accessor: 'paryavekshakName',
                    render: (row) => {
                        const name = row.paryavekshakName;
                        return name || <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '13px' }}>Not Assigned</Typography>;
                    }
                };

                const grandSupervisorCol = {
                    header: 'Samanvaykarta',
                    accessor: 'samanvaykartaName',
                    render: (row) => {
                        const name = row.samanvaykartaName;
                        return name || <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '13px' }}>Not Assigned</Typography>;
                    }
                };

                // Extract and remove Phone Number column to reposition it exactly between Students and Centers / Classes
                const phoneCol = cols.find(col => (col.header || '').toLowerCase() === 'phone number');
                cols = cols.filter(col => (col.header || '').toLowerCase() !== 'phone number');

                if (facultyTypeIndex !== -1) {
                    cols.splice(facultyTypeIndex + 1, 0, grandSupervisorCol, supervisorCol, studentsCol);
                    if (phoneCol) {
                        cols.splice(facultyTypeIndex + 4, 0, phoneCol);
                    }
                } else {
                    cols.push(grandSupervisorCol, supervisorCol, studentsCol);
                    if (phoneCol) {
                        cols.push(phoneCol);
                    }
                }

                cols.push(centersClassesCol);
            }
        }

        return cols;
    }, [userType, hasPermission, deleteMutation.isLoading, reactivateMutation.isLoading, permanentDeleteMutation.isLoading, handleReactivate, handlePermanentDelete, customColumns, handleKvDeleteRequest, isKvFaculty, activeKvTab, orgId]);

    // Handle single user view (e.g. for student role viewing own profile)
    if (userRole === userType && currentUser?.userId && activeTab === 'listView') {
        // Proactive: redirect to profile if the user's role matches the module type
        // This handles the "Student viewing own profile" logic from Students.js
    }

    const handleUserUpdate = (updatedUser) => {
        refetch();
        queryClient.invalidateQueries(['allFacultyHierarchy', orgId]);
        queryClient.invalidateQueries(['classStudentCounts', orgId]);
        // Update the open tab for both KV and non-KV
        addTab(updatedUser, userType);
    };



    const renderProfile = () => {
        const activeUser = openTabs.find(t => t.userId === activeTabId);
        if (!activeUser) return null;

        return (
            <UserProfileModule
                key={activeUser.userId}
                selectedUser={activeUser}
                userType={userType}
                orgId={orgId}
                isKvFaculty={isKvFaculty}
                onUserUpdate={handleUserUpdate}
                onSelectedUserChange={(updatedUser) => addTab(updatedUser, userType)}
                onAddClasses={() => handleOpenClassesModal(activeUser)}
                onOpenSubordinate={(sub) => addTab(sub, 'FACULTY')}
                onDelete={hasPermission(currentPermission, 'DELETE') ? handleKvDeleteRequest : null}
            />
        );
    };

    return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Dynamic Tabs */}
            <Box className="studentmultipletabs" sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}>
                <Box
                    className={`tab ${activeTab === 'listView' ? 'active' : ''}`}
                    onClick={() => setActiveTab('listView')}
                    sx={{ px: 2, py: 1, cursor: 'pointer', bgcolor: '#b2cee9', borderRadius: '4px 4px 0 0', borderRight: '1px solid #ccc' }}
                >
                    {userType.charAt(0) + userType.slice(1).toLowerCase()} Information
                </Box>
                {openTabs.map((tab) => (
                    <Box
                        key={tab.userId}
                        className={`tab ${activeTab === 'userProfiles' && activeTabId === tab.userId ? 'active' : ''}`}
                        onClick={() => setActiveTabId(tab.userId)}
                        sx={{ px: 2, py: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', bgcolor: '#e0e0e0', borderRadius: '4px 4px 0 0', borderRight: '1px solid #ccc', ml: '2px' }}
                    >
                        {(() => {
                            const activeId = tab.studentInfo?.admissionId || tab.facultyInfo?.employeeId || tab.staffInfo?.employeeId;
                            return activeId || tab.user?.firstName || tab.firstName || 'Profile';
                        })()}
                        <span
                            className="close-tab"
                            onClick={(e) => { e.stopPropagation(); closeTab(tab.userId); }}
                            style={{
                                marginLeft: '8px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: '#ccc',
                                color: '#fff',
                                fontSize: '10px',
                                fontWeight: 'bold'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#999'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ccc'}
                        >
                            ✕
                        </span>
                    </Box>
                ))}
            </Box>

            <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
                {error && (
                    <Box sx={{ p: 2, mb: 2, bgcolor: '#ffebee', color: '#c62828', borderRadius: 1, border: '1px solid #ef9a9a' }}>
                        <Typography>Error: {error.message || "Failed to fetch users. Please check your connection or permissions."}</Typography>
                    </Box>
                )}

                {activeTab === 'listView' ? (
                    <>
                        {isKvFaculty && (
                            <Box sx={{
                                display: 'inline-flex',
                                backgroundColor: '#f1f5f9',
                                borderRadius: '10px',
                                padding: '4px',
                                mb: 3,
                                gap: '4px',
                                border: '1px solid #e2e8f0'
                            }}>
                                <Box
                                    onClick={() => navigate('/kv-faculty/samanvaykarta')}
                                    sx={{
                                        px: 3,
                                        py: 1,
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        fontFamily: 'var(--app-font)',
                                        borderRadius: '8px',
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        color: activeKvTab === 'SAMANVAYKARTA' ? '#0070D2' : '#64748b',
                                        backgroundColor: activeKvTab === 'SAMANVAYKARTA' ? '#ffffff' : 'transparent',
                                        boxShadow: activeKvTab === 'SAMANVAYKARTA' ? '0 4px 6px -1px rgb(0 0 0 / 0.08), 0 2px 4px -2px rgb(0 0 0 / 0.08)' : 'none',
                                        userSelect: 'none',
                                        '&:hover': {
                                            color: activeKvTab === 'SAMANVAYKARTA' ? '#0070D2' : '#0f172a',
                                            backgroundColor: activeKvTab === 'SAMANVAYKARTA' ? '#ffffff' : 'rgba(255, 255, 255, 0.4)'
                                        }
                                    }}
                                >
                                    Samanvaykarta
                                </Box>
                                <Box
                                    onClick={() => navigate('/kv-faculty/paryavekshak')}
                                    sx={{
                                        px: 3,
                                        py: 1,
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        fontFamily: 'var(--app-font)',
                                        borderRadius: '8px',
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        color: activeKvTab === 'PARYAVEKSHAK' ? '#0070D2' : '#64748b',
                                        backgroundColor: activeKvTab === 'PARYAVEKSHAK' ? '#ffffff' : 'transparent',
                                        boxShadow: activeKvTab === 'PARYAVEKSHAK' ? '0 4px 6px -1px rgb(0 0 0 / 0.08), 0 2px 4px -2px rgb(0 0 0 / 0.08)' : 'none',
                                        userSelect: 'none',
                                        '&:hover': {
                                            color: activeKvTab === 'PARYAVEKSHAK' ? '#0070D2' : '#0f172a',
                                            backgroundColor: activeKvTab === 'PARYAVEKSHAK' ? '#ffffff' : 'rgba(255, 255, 255, 0.4)'
                                        }
                                    }}
                                >
                                    Paryavekshak
                                </Box>
                                <Box
                                    onClick={() => navigate('/kv-faculty/nirvahak')}
                                    sx={{
                                        px: 3,
                                        py: 1,
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        fontFamily: 'var(--app-font)',
                                        borderRadius: '8px',
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        color: activeKvTab === 'NIRVAHAK' ? '#0070D2' : '#64748b',
                                        backgroundColor: activeKvTab === 'NIRVAHAK' ? '#ffffff' : 'transparent',
                                        boxShadow: activeKvTab === 'NIRVAHAK' ? '0 4px 6px -1px rgb(0 0 0 / 0.08), 0 2px 4px -2px rgb(0 0 0 / 0.08)' : 'none',
                                        userSelect: 'none',
                                        '&:hover': {
                                            color: activeKvTab === 'NIRVAHAK' ? '#0070D2' : '#0f172a',
                                            backgroundColor: activeKvTab === 'NIRVAHAK' ? '#ffffff' : 'rgba(255, 255, 255, 0.4)'
                                        }
                                    }}
                                >
                                    Nirvahak
                                </Box>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="h6">
                                    {isKvFaculty
                                        ? activeKvTab.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ') + ' Faculty'
                                        : fixedFacultyType
                                            ? fixedFacultyType.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')
                                            : `All ${userType.charAt(0) + userType.slice(1).toLowerCase()}${userType === 'FACULTY' || userType === 'STAFF' ? '' : 's'}`
                                    }
                                </Typography>
                                {userType === 'STUDENT' ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2">Grade:</Typography>
                                        {loadingClasses ? <CircularProgress size={20} /> : (
                                            <Select
                                                size="small"
                                                value={selectedClass || ''}
                                                onChange={(e) => setSelectedGrade(userType, e.target.value)}
                                                displayEmpty
                                            >
                                                {classesData?.length === 0 ? (
                                                    <MenuItem value="">No classes found</MenuItem>
                                                ) : (
                                                    classesData?.map(cls => (
                                                        <MenuItem key={cls.value} value={cls.value}>{cls.label}</MenuItem>
                                                    ))
                                                )}
                                            </Select>
                                        )}
                                        <Typography variant="body2" sx={{ ml: 1, whiteSpace: 'nowrap', color: 'text.secondary' }}>
                                            (Total Students: <strong>{filteredItems.length}</strong>)
                                        </Typography>
                                    </Box>
                                ) : userType === 'FACULTY' ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {!fixedFacultyType && !isKvFaculty && (
                                            <>
                                                <Typography variant="body2" sx={{ ml: 1.5, borderLeft: '1px solid #e2e8f0', pl: 1.5 }}>Faculty Type:</Typography>
                                                <Select
                                                    size="small"
                                                    value={selectedFacultyType}
                                                    onChange={(e) => setSelectedFacultyType(e.target.value)}
                                                >
                                                    <MenuItem value="ALL">All Faculty</MenuItem>
                                                    {uniqueFacultyTypes.map(type => (
                                                        <MenuItem key={type} value={type}>
                                                            {type.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </>
                                        )}
                                        <Typography variant="body2" sx={{ ml: 1, whiteSpace: 'nowrap', color: 'text.secondary' }}>
                                            (Total {isKvFaculty ? activeKvTab.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ') : fixedFacultyType ? fixedFacultyType.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ') : 'Faculty'}: <strong>{filteredItems.length}</strong>
                                            {isKvFaculty && (
                                                <>
                                                    {" | Total Students: "}<strong>{totalStudentsCount}</strong>
                                                </>
                                            )}
                                            )
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ ml: 1.5, whiteSpace: 'nowrap', color: 'text.secondary', borderLeft: '1px solid #e2e8f0', pl: 1.5 }}>
                                            (Total {userType === 'STAFF' ? 'Staff' : 'Admins'}: <strong>{filteredItems.length}</strong>)
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    size="small"
                                    label="Search"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                />
                                {(!isKvFaculty || (isKvFaculty && (userRole === 'ADMIN' || (userRole === 'FACULTY' && (facultyInfo?.facultyType === 'SAMANVAYKARTA' || facultyInfo?.facultyType === 'PARYAVEKSHAK'))))) && (
                                    <Box sx={{ display: 'flex', gap: 2 }} className="button-group">
                                        <Tooltip title="Add New User">
                                            <IconButton className="stuentbutton" sx={{ border: '1px solid rgb(207, 205, 204)', borderRadius: '4px', padding: '9px 16px', color: '#0070D2' }} onClick={() => {
                                                if (isKvFaculty) {
                                                    navigate(`/Faculty/registration`, { state: { orgId, lockedFacultyType: activeKvTab } });
                                                } else {
                                                    const rm = { STUDENT: '/Students', FACULTY: '/Faculty', STAFF: '/Staff', ADMIN: '/Admin' };
                                                    navigate(`${rm[userType] || `/${userType.toLowerCase()}`}/registration`, { state: { orgId, selectedClass } });
                                                }
                                            }}>
                                                <AddIcon />
                                            </IconButton>
                                        </Tooltip>

                                        {userRole !== 'FACULTY' && (
                                            <Tooltip title="Bulk Import">
                                                <IconButton className="stuentbutton" sx={{ border: '1px solid rgb(207, 205, 204)', borderRadius: '4px', padding: '9px 16px', color: '#0070D2' }} onClick={() => {
                                                    if (userType === 'FACULTY' || userType === 'STAFF') {
                                                        navigate('/bulk-import', { state: { orgId, importType: userType.toLowerCase() } });
                                                    } else {
                                                        const rm = { STUDENT: '/Students', ADMIN: '/Admin' };
                                                        navigate(`${rm[userType] || `/${userType.toLowerCase()}`}/bulk-import`, { state: { orgId } });
                                                    }
                                                }}>
                                                    <ImportExportIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}

                                        {hasPermission(currentPermission, 'DELETE') && (
                                            <Tooltip title="Delete Selected">
                                                <IconButton
                                                    className="stuentbutton"
                                                    sx={{ border: '1px solid rgb(207, 205, 204)', borderRadius: '4px', padding: '9px 16px', color: '#0070D2' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        handleDeleteSelected();
                                                    }}
                                                    disabled={selectedUsers.length === 0 || deleteMutation.isLoading}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}

                                        {['STUDENT', 'FACULTY', 'STAFF', 'ADMIN'].includes(userType) && userRole !== 'FACULTY' && (
                                            <Box>
                                                <Tooltip title="Firebase Actions">
                                                    <IconButton
                                                        className="stuentbutton"
                                                        sx={{ border: '1px solid rgb(207, 205, 204)', borderRadius: '4px', padding: '9px 16px', color: '#0070D2' }}
                                                        disabled={selectedUsers.length === 0} onClick={handleFirebaseMenuOpen}
                                                    >
                                                        <SecurityIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Menu
                                                    anchorEl={firebaseMenuAnchorEl}
                                                    open={Boolean(firebaseMenuAnchorEl)}
                                                    onClose={handleFirebaseMenuClose}
                                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                                                >
                                                    {isCheckingStatus ? (
                                                        <MenuItem disabled sx={{ justifyContent: 'center', py: 2, minWidth: 150 }}>
                                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                                            <Typography variant="body2">Checking status...</Typography>
                                                        </MenuItem>
                                                    ) : (
                                                        <>
                                                            {selectionFirebaseStatus.noAccount > 0 && (
                                                                <MenuItem onClick={() => { handleBulkSignup(); handleFirebaseMenuClose(); }} sx={{ color: '#0070D2' }}>
                                                                    <PersonAddAltIcon sx={{ mr: 1 }} /> Enable Signup {selectedUsers.length > 1 ? `(${selectionFirebaseStatus.noAccount})` : ''}
                                                                </MenuItem>
                                                            )}
                                                            {selectionFirebaseStatus.hasAccount > 0 && (
                                                                <MenuItem onClick={() => { handleBulkRemoveFirebase(); handleFirebaseMenuClose(); }} sx={{ color: '#0070D2' }}>
                                                                    <PersonRemoveIcon sx={{ mr: 1 }} /> Disable Signup {selectedUsers.length > 1 ? `(${selectionFirebaseStatus.hasAccount})` : ''}
                                                                </MenuItem>
                                                            )}
                                                            {selectionFirebaseStatus.noAccount === 0 && selectionFirebaseStatus.hasAccount === 0 && (
                                                                <MenuItem disabled>No users to process</MenuItem>
                                                            )}
                                                        </>
                                                    )}
                                                </Menu>
                                            </Box>
                                        )}

                                        <Tooltip title="Refresh Data">
                                            <IconButton className="stuentbutton" sx={{ border: '1px solid rgb(207, 205, 204)', borderRadius: '4px', padding: '9px 16px', color: '#0070D2' }} onClick={() => {
                                                refetch();
                                                queryClient.invalidateQueries(['allFacultyHierarchy', orgId]);
                                                queryClient.invalidateQueries(['classStudentCounts', orgId]);
                                            }}>
                                                <RefreshIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {userType === 'STUDENT' && classesData?.length === 0 && (
                            <Box sx={{
                                mb: 3,
                                p: 2,
                                bgcolor: '#fffbeb',
                                border: '1px solid #fef3c7',
                                borderRadius: 2,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography color="#b45309" variant="body2">
                                        <strong>⚠️ No Classes Configured:</strong> This organization does not have any classes set up yet. You must create a class before you can view, register, or import students.
                                    </Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    color="warning"
                                    size="small"
                                    onClick={() => navigate('/classes')}
                                    sx={{ bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' }, textTransform: 'none', fontWeight: 'bold' }}
                                >
                                    ➕ Go Create a Class
                                </Button>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', gap: 0, height: '100%' }}>
                            {/* Table */}
                            <Box sx={{
                                flex: 1,
                                minWidth: 0,
                                overflowX: 'auto',
                            }}>
                                <Table
                                    data={sortedMappedItems}
                                    columns={defaultColumns}
                                    loading={loadingUsers}
                                    enableCheckboxes={!isKvFaculty && (hasPermission(currentPermission, 'DELETE') || ['STUDENT', 'FACULTY', 'STAFF', 'ADMIN'].includes(userType))}
                                    onSelectionChange={setSelectedUsers}
                                    externalSelections={selectedUsers}
                                    enableSorting={true}
                                    enableColumnToggle={false}
                                    enableExport={false}
                                    onRowClick={(row) => {
                                        const status = row.user?.userStatus || row.userStatus || 'ACTIVE';
                                        if (status === 'INACTIVE' || status === 'inactive') {
                                            toast.warn("Deactivated profiles cannot be viewed or edited.");
                                            return;
                                        }
                                        addTab(row, userType);
                                    }}
                                    hoverable
                                    striped
                                    className={isKvFaculty ? "kv-table-premium" : ""}
                                />
                            </Box>
                        </Box>
                    </>
                ) : (
                    renderProfile()
                )}
            </Box>



            {/* ── KV Delete Confirmation Dialog ─────────────────────────────── */}
            <Dialog
                open={kvDeleteDialog.open}
                onClose={() => !kvDeleting && setKvDeleteDialog({ open: false, user: null, hasSubordinates: false, subordinates: [] })}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, color: kvDeleteDialog.hasSubordinates ? '#d97706' : 'text.primary' }}>
                    {kvDeleteDialog.hasSubordinates ? '⚠️ Reassign & Deactivate' : '🗑️ Confirm Deactivation'}
                </DialogTitle>
                <DialogContent>
                    {kvDeleteDialog.hasSubordinates ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Typography variant="body1">
                                {targetRole === 'NIRVAHAK' ? (
                                    <>
                                        <strong>{getFullName(kvDeleteDialog.user)}</strong> has <strong>{kvDeleteDialog.subordinates?.length}</strong> class(es) assigned to them.
                                    </>
                                ) : (
                                    <>
                                        <strong>{getFullName(kvDeleteDialog.user)}</strong> has <strong>{kvDeleteDialog.subordinates?.length}</strong> team member(s) assigned under them.
                                    </>
                                )}
                            </Typography>
                            <>
                                <Typography variant="body2" color="text.secondary">
                                    {targetRole === 'NIRVAHAK'
                                        ? 'Please select a replacement Nirvahak to reassign all classes to, or select None to delete without reassigning:'
                                        : `Please select a replacement supervisor to reassign all subordinates to, or select None to clear their supervisor:`}
                                </Typography>
                                <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                                    <InputLabel id="replacement-supervisor-label">Replacement {replacementLabel}</InputLabel>
                                    <Select
                                        labelId="replacement-supervisor-label"
                                        value={replacementSupervisorId}
                                        label={`Replacement ${replacementLabel}`}
                                        onChange={(e) => setReplacementSupervisorId(e.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em style={{ color: '#94a3b8' }}>Select replacement {replacementLabel.toLowerCase()}</em>
                                        </MenuItem>
                                        <MenuItem value="none" sx={{ fontWeight: 600, color: '#dc2626' }}>
                                            None (Unassign / Clear)
                                        </MenuItem>
                                        {replacementOptions.map(opt => (
                                            <MenuItem key={opt.id} value={opt.id}>
                                                {opt.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {replacementOptions.length === 0 && (
                                    <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block', fontWeight: 600 }}>
                                        ⚠️ No other {replacementLabel}s are registered in this organization. Only "None" is selectable.
                                    </Typography>
                                )}
                            </>
                        </Box>
                    ) : (
                        <Typography variant="body1">
                            Are you sure you want to deactivate <strong>{getFullName(kvDeleteDialog.user)}</strong>?
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setKvDeleteDialog({ open: false, user: null, hasSubordinates: false, subordinates: [] })}
                        disabled={kvDeleting}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Cancel
                    </Button>
                    {kvDeleteDialog.hasSubordinates ? (
                        <Button
                            variant="contained"
                            onClick={handleKvDeleteConfirm}
                            disabled={kvDeleting || !replacementSupervisorId}
                            sx={{ bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' }, textTransform: 'none', fontWeight: 700 }}
                        >
                            {kvDeleting ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
                            {kvDeleting ? 'Reassigning & Deactivating…' : 'Reassign & Deactivate'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleKvDeleteConfirm}
                            disabled={kvDeleting}
                            sx={{ bgcolor: '#0070d2', '&:hover': { bgcolor: '#005fb2' }, textTransform: 'none', fontWeight: 700 }}
                        >
                            {kvDeleting ? (
                                <>
                                    <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                                    Deactivating…
                                </>
                            ) : (
                                <DeleteIcon />
                            )}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <AdminAssistant dashboardStats={{ [userType.toLowerCase() + 's']: usersData?.totalItems || 0 }} />

            {/* Signup/Removal Summary Modal */}
            <Dialog
                open={summaryInfo.isOpen}
                onClose={() => setSummaryInfo(prev => ({ ...prev, isOpen: false }))}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{summaryInfo.title}</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip
                            icon={<CheckCircleIcon />}
                            label={`${summaryInfo.successItems.length} Success`}
                            sx={{ bgcolor: 'rgba(0, 112, 210, 0.1)', color: '#0070D2', fontWeight: 600 }}
                            size="small"
                        />
                    </Box>
                </DialogTitle>

                <DialogContent dividers sx={{ bgcolor: '#f8fafc' }}>
                    {summaryInfo.type === 'ENABLE' && (
                        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderLeft: '4px solid #4f46e5', bgcolor: '#fff' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <SecurityIcon sx={{ mr: 1, color: '#4f46e5', fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4f46e5' }}>
                                    Default Credentials
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2">
                                    Password: <strong>{summaryInfo.password}</strong>
                                </Typography>
                                <Tooltip title="Copy Password">
                                    <IconButton size="small" onClick={() => {
                                        navigator.clipboard.writeText(summaryInfo.password);
                                        toast.success("Password copied!");
                                    }}>
                                        <ContentCopyIcon fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                Users can use their email as the username.
                            </Typography>
                        </Paper>
                    )}

                    {summaryInfo.messagesSentCount > 0 && (
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', px: 1 }}>
                            <InfoIcon sx={{ mr: 1, color: '#0070D2', fontSize: 18 }} />
                            <Typography variant="body2" sx={{ color: '#0070D2', fontWeight: 500 }}>
                                Sent <strong>{summaryInfo.messagesSentCount}</strong> welcome messages to Inbox Messages.
                            </Typography>
                        </Box>
                    )}

                    {summaryInfo.alreadyExistsItems?.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="overline" sx={{ px: 1, color: '#0070D2', opacity: 0.8, fontWeight: 700 }}>
                                Already Had Active Accounts
                            </Typography>
                            <Paper variant="outlined" sx={{ maxHeight: 120, overflow: 'auto', mt: 0.5, bgcolor: 'rgba(0,112,210,0.02)' }}>
                                <List dense disablePadding>
                                    {summaryInfo.alreadyExistsItems.map((item, idx) => (
                                        <React.Fragment key={idx}>
                                            <ListItem>
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <InfoIcon sx={{ color: '#0070D2', fontSize: 18 }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={item.email}
                                                    secondary={item.name}
                                                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                                />
                                            </ListItem>
                                            {idx < summaryInfo.alreadyExistsItems.length - 1 && <Divider component="li" />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            </Paper>
                        </Box>
                    )}

                    <Typography variant="overline" sx={{ px: 1, color: 'text.secondary', fontWeight: 700 }}>
                        {summaryInfo.type === 'ENABLE' ? 'Successfully Enabled' : 'Successfully Removed'}
                    </Typography>
                    <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', mt: 0.5 }}>
                        <List dense disablePadding>
                            {summaryInfo.successItems.length > 0 ? summaryInfo.successItems.map((item, idx) => (
                                <React.Fragment key={idx}>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <CheckCircleIcon sx={{ color: '#0070D2' }} fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item.email}
                                            secondary={item.name}
                                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                        />
                                    </ListItem>
                                    {idx < summaryInfo.successItems.length - 1 && <Divider component="li" />}
                                </React.Fragment>
                            )) : (
                                <ListItem><ListItemText primary="No users were successfully processed." /></ListItem>
                            )}
                        </List>
                    </Paper>

                    {summaryInfo.failedItems.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="overline" sx={{ px: 1, color: '#f44336', fontWeight: 700 }}>
                                Errors / Failed
                            </Typography>
                            <Paper variant="outlined" sx={{ maxHeight: 120, overflow: 'auto', mt: 0.5, borderColor: '#ffcdd2' }}>
                                <List dense disablePadding>
                                    {summaryInfo.failedItems.map((item, idx) => (
                                        <React.Fragment key={idx}>
                                            <ListItem>
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <ErrorIcon color="error" fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={item.email}
                                                    secondary={item.error}
                                                    primaryTypographyProps={{ variant: 'body2', color: 'error' }}
                                                />
                                            </ListItem>
                                            {idx < summaryInfo.failedItems.length - 1 && <Divider component="li" />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            </Paper>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => setSummaryInfo(prev => ({ ...prev, isOpen: false }))}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Manage Classes Dialog */}
            <Dialog
                open={openClassesModal}
                onClose={() => setOpenClassesModal(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 800, color: '#0f172a', pb: 1 }}>
                    🏫 Assign Centers to {selectedUserForClasses ? getFullName(selectedUserForClasses) : 'Nirvahak'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select the tuition center classes this Nirvahak oversees. You can search and multi-select classes.
                    </Typography>

                    {/* Removable chips of currently selected classes */}
                    {selectedClasses.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px dashed #cbd5e1' }}>
                            {selectedClasses.map((clsId) => {
                                const classObj = classes.find(c => c.value === clsId);
                                const label = classObj ? classObj.label : clsId;
                                return (
                                    <Chip
                                        key={clsId}
                                        label={label}
                                        size="small"
                                        onDelete={() => {
                                            setSelectedClasses(prev => prev.filter(item => item !== clsId));
                                        }}
                                        sx={{ bgcolor: '#e0ecfb', color: '#0070D2', fontWeight: 600 }}
                                    />
                                );
                            })}
                        </Box>
                    )}

                    <TextField
                        fullWidth
                        size="small"
                        placeholder="🔍 Search classes/centers..."
                        value={classesSearchTerm}
                        onChange={(e) => setClassesSearchTerm(e.target.value)}
                        sx={{ mb: 2 }}
                    />

                    <Box sx={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff' }}>
                        {loadingAllClasses ? (
                            <Box sx={{ p: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                                <CircularProgress size={28} sx={{ color: '#0070D2' }} />
                                <Typography variant="body2" color="text.secondary">Loading available classes...</Typography>
                            </Box>
                        ) : filteredClassesList.length > 0 ? (
                            <List dense>
                                {filteredClassesList.map((cls) => {
                                    const isChecked = selectedClasses.includes(cls.value);
                                    return (
                                        <ListItem
                                            key={cls.value}
                                            button
                                            onClick={() => {
                                                if (isChecked) {
                                                    setSelectedClasses(prev => prev.filter(item => item !== cls.value));
                                                } else {
                                                    setSelectedClasses(prev => [...prev, cls.value]);
                                                }
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <Checkbox
                                                    edge="start"
                                                    checked={isChecked}
                                                    tabIndex={-1}
                                                    disableRipple
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={cls.label}
                                                primaryTypographyProps={{ fontWeight: isChecked ? 600 : 500, color: isChecked ? '#0070D2' : '#334155' }}
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        ) : (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">No matching classes found</Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setOpenClassesModal(false)}
                        sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}
                        disabled={savingClasses}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveClasses}
                        variant="contained"
                        disabled={savingClasses}
                        sx={{ bgcolor: '#0070D2', '&:hover': { bgcolor: '#005FB2' }, textTransform: 'none', fontWeight: 700, px: 3 }}
                    >
                        {savingClasses ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Custom Confirmation Dialog */}
            <Dialog
                open={confirmationDialog.isOpen}
                onClose={() => setConfirmationDialog(prev => ({ ...prev, isOpen: false }))}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>{confirmationDialog.title}</DialogTitle>
                <DialogContent>
                    <Typography>{confirmationDialog.message}</Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setConfirmationDialog(prev => ({ ...prev, isOpen: false }))}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (confirmationDialog.onConfirm) confirmationDialog.onConfirm();
                            setConfirmationDialog(prev => ({ ...prev, isOpen: false }));
                        }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserModule;
